import { registerService } from './lib';

export type WealthTxStatus =
  | 'DONE'
  | 'FAILED'
  | 'PENDING'
  | 'CREATED'
  | 'SENT'
  | 'VERIFIED'
  | 'WAITING_FOR_MFA'
  | 'REJECTED'
  | 'ADMIN_CANCELED'
  | 'FAILED_BEFORE_SENDING'
  | 'CANCELLED' // P2p
  | 'PAID' // P2p
  | 'RELEASED' // P2p
  | 'COMPLETED' // P2p
  | 'DISPUTED' // P2p
  | 'finalized'
  | 'rejected';

export type WealthTxSide = 'BUY' | 'SELL';

export type WealthUserAddress = {
  key: string;
  name: string;
  address: string;
  network: string;
  status: string;
  mfa_key: string;
  created_at: string;
  tag?: string;
};

export type WealthCreateUserAddressParams = {
  name: string;
  address: string;
  network: string;
  tag?: string;
};

export type WealthMission = {
  key: string;
  expires_at: number;
  invites_count: number;
  steps: {
    invites_required: number;
    months_no_commission: number;
    reward: number;
    status: 'none' | 'claimable' | 'claimed';
  }[];
};

export type WealthCoin = {
  symbol: string;
  name: string;
  precision: number;
  slug: string;
  image: string;
  color: string;
  iraqi_name: string;
  price_usdt: string;
  price_change_percent: string;
  volume_usdt: string;
  on_market: boolean;
  price_iqd: string;
  volume_iqd: string;
  withdraw_enabled: boolean;
  deposit_enabled: boolean;
  min_internal_transfer: string;
  popularity: number;
  coingecko_name: string;
  is_marketplace_active?: boolean;
  market_data: {
    price: number;
    low_24h: number;
    high_24h: number;
    ath: number;
    price_iqd: number;
    low_24h_iqd: number;
    high_24h_iqd: number;
    ath_iqd: number;
    price_percent: number;
    change_1h: number;
    change_24h: number;
    change_7d: number;
    market_cap: number;
    volume_24h: number;
    circulating_supply: number;
    max_supply: number;
    chart_slug: string;
  };
};

export type WealthPair = {
  slug: string;
  chart_slug: string;
  base_coin: string;
  quote_coin: string;
  base_precision: number;
  quote_precision: number;
  price_precision: number;
  last_price: null | string;
  price_change_percent: null | string;
  volume: string;
};

export type WealthBalanceSummary = {
  coin: string;
  balance: string;
  balance_usdt: string;
  balance_iqd: string;
  percentage_of_total: number;
};

export type WealthBalanceDetails = WealthBalanceSummary & {
  available: string;
  in_order: string;
};

export type WealthBalances = {
  total_usdt: string;
  total_iqd: string;
  balances: WealthBalanceDetails[];
  other?: WealthBalanceSummary;
};

export type WealthOrderType = 'MARKET' | 'LIMIT';

export type WealthOrderState =
  | 'OPEN'
  | 'PARTIALLY_FILLED'
  | 'FILLED'
  | 'CANCELLED';

export type WealthCreateOrderParams = {
  pair: string;
  side: WealthTxSide;
  base_amount?: string;
  quote_amount?: string;
  price?: string;
  type: WealthOrderType;
  client_id: string;
};

export type WealthOrder = {
  pair: string;
  base_coin: string;
  quote_coin: string;
  type: WealthOrderType;
  side: WealthTxSide;
  base_amount: string;
  quote_amount: string;
  price: string;
  executed_base_amount: string;
  executed_quote_amount: string;
  executed_price?: null | string;
  filled_percent: number;
  state: WealthOrderState;
  created_at: number;
  updated_at: number;
  tracking_code: string;
  client_id: string;
};

export type WealthCryptoDeposit = {
  amount: string;
  coin: string;
  created_at: number;
  updated_at: number;
  network: string;
  status: WealthTxStatus;
  tracking_code: string;
  tx_hash: null | string;
  transaction_url: null | string;
};

export type WealthIqdDeposit = {
  amount: string;
  created_at: number;
  updated_at: number;
  status: WealthTxStatus;
  tracking_code: string;
  reference_id: string;
};

export type WealthCryptoWithdraw = WealthUserDetails & {
  address: string;
  amount: string;
  coin: string;
  created_at: number;
  updated_at: number;
  fee: string;
  net_amount: string;
  network: string;
  status: WealthTxStatus;
  tracking_code: string;
  tx_hash: null | string;
  transaction_url: null | string;
  mfa_key: null | string;
  trace_id: null | string;
};

export type WealthIqdWithdraw = WealthUserDetails & {
  amount: string;
  created_at: number;
  updated_at: number;
  fee: string;
  net_amount: string;
  status: WealthTxStatus;
  tracking_code: string;
  mfa_key: string | null;
};

export type WealthCreateCryptoWithdrawParams = {
  coin: string;
  address: string;
  network: string;
  amount: string;
  tag?: string;
};

export type WealthIqdCashWithdraw = {
  tracking_code: string;
  user_id: string;
  amount: string;
  net_amount: string;
  fee: string;
  status: WealthTxStatus;
  mfa_key: string | null;
  transaction_type: string;
  created_at: number;
  updated_at: number;
  payment_method: WealthCashLocation;
  otp: string | null;
};

export type WealthIqdCashDeposit = {
  tracking_code: string;
  user_id: string;
  amount: string;
  net_amount: string;
  fee: string;
  status: WealthTxStatus;
  created_at: number;
  updated_at: number;
  payment_method: WealthCashLocation;
};

export type WealthCreateIqdCashDepositParams = {
  amount: string;
  payment_method_id: string;
};

export type WealthCashLocation = {
  id: string;
  merchant_name: string;
  name: string;
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  phone: string;
};

export type WealthConvertPreview = {
  from_coin: string;
  to_coin: string;
  from_amount: string;
  to_amount: string;
  price: string;
  is_verified: boolean;
  is_sell: boolean;
};

export type WealthConvert = WealthConvertPreview & {
  created_at: number;
  updated_at: number;
  status: string;
  user_id: string;
  tracking_code: string;
  type: string;
};

export type WealthTrade = {
  created_at: number;
  id_hash: string;
  is_sell: boolean;
  pair: string;
  price: string;
  base_amount: string;
  base_coin: string;
  quote_amount: string;
  quote_coin: string;
  side: WealthTxSide;
  type: WealthOrderType;
};

export type WealthMarketplaceMerchantStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'SUSPENDED';

export type WealthMarketplaceMerchant = {
  user_id: string;
  status: WealthMarketplaceMerchantStatus;
  display_name: string;
  bio?: string | null;
  cash_iqd_balance?: string;
  balance_threshold?: string;
  has_cash_payment_method?: boolean;
  completion_rate: string;
  total_trades: number;
  avg_release_time_seconds: number;
  total_cash_pickups_completed?: number;
  total_cash_deposits_completed?: number;
  created_at: number;
  updated_at: number;
  accepts_cash_operations?: boolean;
};

export type WealthMarketplaceDisputeResolution =
  | 'RELEASE_TO_BUYER'
  | 'REFUND_TO_SELLER';

export type WealthMarketplaceDispute = {
  trade_code: string;
  raised_by: string;
  reason: string;
  status: 'OPEN' | 'RESOLVED';
  resolution: WealthMarketplaceDisputeResolution | null;
  resolved_by: string | null;
  resolution_note: string;
  created_at: number;
  updated_at: number;
  trade: WealthMarketplaceTrade;
};

export type WealthDeposit = WealthUserDetails & {
  amount: string;
  coin: string;
  created_at: number;
  updated_at: number;
  network?: string;
  status: string | WealthTxStatus;
  tracking_code: string;
  tx_hash: null | string;
  transaction_url: null | string;
};

export type WealthIqdPrice = {
  iqd_to_usdt_price: string;
  usdt_to_iqd_price: string;
};

export type WealthUserBalancePanel = {
  total_usdt: string;
  total_iqd: string;
  balances: WealthBalanceSummary[];
  other?: WealthBalanceSummary;
};

export type WealthUserBalanceHistory = {
  id: number;
  user_id: string;
  coin: string;
  total: string;
  available: string;
  total_diff: string;
  available_diff: string;
  last_change_type: string;
  last_change_reference: string;
  previous_user_balance: number;
  created_at: number;
  reference?: WealthConvert;
};

export type WealthMarketplacePaymentMethod = {
  slug: string;
  name: string;
  type: 'BANK_TRANSFER' | 'CASH' | 'MOBILE_WALLET';
  icon: string;
  is_active: boolean;
};

export type WealthCreateMarketplaceAdParams = {
  ad_type: WealthTxSide;
  coin: string;
  price: string;
  min_amount: string;
  max_amount: string;
  available_quantity: string;
  payment_method_slugs: string[];
  terms_and_conditions?: string;
  auto_reply_message?: string;
  time_limit?: number;
};

export type WealthMarketplaceAdStatus = 'ACTIVE' | 'PAUSED' | 'CLOSED';

export type WealthMarketplaceAd = Omit<
  WealthCreateMarketplaceAdParams,
  'payment_method_slugs'
> & {
  tracking_code: string;
  merchant: WealthMarketplaceMerchant;
  payment_methods: WealthMarketplacePaymentMethod[];
  status: WealthMarketplaceAdStatus;
  created_at: number;
  updated_at: number;
};

export type WealthMarketplaceTrade = {
  tracking_code: string;
  advertisement_code: string;
  buyer_id: string;
  seller_id: string;
  coin: string;
  crypto_amount: string;
  fiat_amount: string;
  price: string;
  payment_method: {
    payment_method: WealthMarketplacePaymentMethod;
    account_details: Record<string, string>;
    is_active: boolean;
    created_at: number;
  } | null;
  merchant: WealthMarketplaceMerchant | null;
  status: WealthTxStatus;
  payment_proof?: string;
  payment_deadline: number | null;
  release_deadline: number | null;
  created_at: number;
  updated_at: number;
};

export type WealthNetwork = {
  iraqi_name: string;
  name: string;
  slug: string;
  standard: string;
  supports_tag: boolean;
  min_confirm?: number;
  address_regex?: string;
  confirmation_time?: number;
};

export type WealthCreateConvertAmount =
  | {
      from_amount: string;
      to_amount?: never;
    }
  | {
      from_amount?: never;
      to_amount: string;
    };

export type WealthCreateConvertParams = {
  from_coin: string;
  to_coin: string;
} & WealthCreateConvertAmount;

export type WealthOrderbookOrder = {
  price: string;
  total_base: string;
  total_quote: string;
  depth_percent: number;
};

export type WealthOrderbook = {
  pair: string;
  buys: WealthOrderbookOrder[];
  sells: WealthOrderbookOrder[];
  buy_percent: number;
  sell_percent: number;
};

export type WealthDepositNetwork = {
  network: string;
  min_deposit_amount: string;
  supports_tag?: boolean;
};

export type WealthWithdrawNetwork = {
  network: string;
  fee: string;
  min_withdraw_amount: string;
  temporarily_disabled?: boolean;
  supports_tag?: boolean;
};

export type WealthCreateIqdCashWithdrawParams = {
  amount: string;
  payment_method_id: string;
};

export type WealthGetCryptoWithdrawParams = {
  internal?: boolean;
};

export type WealthGetOrdersParams = {
  state?: 'open' | 'closed';
  pair?: string;
};

export type WealthGetTradesParams = {
  pair?: string;
  user_only?: boolean;
};

export type WealthVote = 'bullish' | 'bearish';

export type WealthUserDetails = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone_number: string | null;
};

export type WealthMerchantCashPickup = WealthUserDetails & {
  tracking_code: string;
  amount: string;
  net_amount: string;
  fee: string;
  status: WealthTxStatus;
  mfa_key: string;
  transaction_type: string;
  created_at: number;
  updated_at: number;
  payment_method: WealthCashLocation;
};

export type WealthMerchantCashDeposit = WealthUserDetails & {
  tracking_code: string;
  amount: string;
  net_amount: string;
  fee: string;
  status: WealthTxStatus;
  created_at: number;
  updated_at: number;
};

export type WealthMerchantCashPickupHistory = {
  id: number;
  amount: string;
  balance_after: string;
  type: 'PICKUP' | 'DEPOSIT';
  reference_id: string;
  created_at: number;
};

export type WealthCreateWithdrawParams = {
  coin: string;
  amount: string;
};
declare module './lib' {
  interface ServiceRegistry {
    wealth: ServiceEndpoints<{
      'get:networks/': {
        response: WealthNetwork[];
      };
      'get:user-addresses/': {
        response: WealthUserAddress[];
      };
      'post:user-addresses/create/': {
        params: WealthCreateUserAddressParams;
        response: WealthUserAddress;
      };
      'delete:user-addresses/{slug}/': {
        params: {
          slug: string;
        };
      };
      'patch:user-addresses/{slug}/': {
        params: {
          slug: string;
          name: string;
        };
      };
      'get:mission/': {
        response: WealthMission;
      };
      'post:missions/claim-reward/': {
        params: {
          mission_key: string;
          step: number;
        };
      };
      'post:vote/': {
        params: { coin: string; vote: WealthVote };
      };
      'post:crypto-profit/': {
        params: {
          symbol: string;
          amount: number;
          date: number;
        };
        response: {
          current_value_usd: string;
          current_price: number;
          purchase_price: number;
          bought_price: number;
        };
      };
      'get:coin/{slug}/bullish-percentage/': {
        params: {
          slug: string;
        };
        response: {
          percentage_bullish: number;
        };
      };
      'get:coins/': {
        response: WealthCoin[];
      };
      'get:coins/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthCoin;
      };
      'get:pairs/': {
        response: WealthPair[];
      };
      'get:balances/': {
        params: {
          top_n?: number;
        };
        response: WealthBalances;
      };
      'get:balances/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthBalanceDetails;
      };
      'get:deposits/iqd/': {
        response: WealthIqdDeposit[];
      };
      'get:deposits/crypto/': {
        response: WealthCryptoDeposit[];
      };
      'post:deposits/create/': {
        params: {
          coin: string;
          amount: string;
          network: 'ethereum';
        };
      };
      'get:deposits/iqd/cash/locations/': {
        response: WealthCashLocation[];
      };
      'get:deposits/iqd/cash/': {
        params: {
          status?: string;
        };
        response: WealthIqdCashDeposit[];
      };
      'get:deposits/iqd/cash/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthIqdCashDeposit;
      };
      'post:deposits/iqd/cash/create/': {
        params: WealthCreateIqdCashDepositParams;
        response: WealthIqdCashDeposit;
      };
      'post:deposits/iqd/cash/cancel/': {
        params: {
          tracking_code: string;
        };
        response: { message: string };
      };
      'get:converts/': {
        response: WealthConvert[];
      };
      'get:orders/': {
        params: WealthGetOrdersParams;
        response: WealthOrder[];
      };
      'get:trades/': {
        params: WealthGetTradesParams;
        response: WealthTrade[];
      };
      'get:withdraws/iqd/': {
        response: WealthIqdWithdraw[];
      };
      'get:withdraws/crypto/': {
        params: WealthGetCryptoWithdrawParams;
        response: WealthCryptoWithdraw[];
      };
      'get:withdraws/iqd/cash/': {
        params: {
          status?: string;
        };
        response: WealthIqdCashWithdraw[];
      };
      'get:withdraws/crypto/{slug}/verify/': {
        params: {
          slug: string;
        };
      };
      'post:withdraws/create/': {
        params: WealthCreateWithdrawParams;
      };
      'post:withdraws/iqd/create/': {
        params: WealthCreateWithdrawParams;
        response: WealthIqdWithdraw;
      };
      'post:withdraws/crypto/internal-transfer/': {
        params: {
          coin: string;
          receiver: string;
          amount: string;
        };
        response: WealthCryptoWithdraw;
      };
      'get:withdraws/limit/': {
        response: {
          total_withdrawn_last_24_hours: string;
          withdraw_limit: string;
        };
      };
      'post:withdraws/crypto/create/': {
        params: WealthCreateCryptoWithdrawParams;
        response: WealthCryptoWithdraw;
      };
      'get:withdraws/cash/locations/': {
        params: {
          amount?: number;
        };
        response: WealthCashLocation[];
      };
      'post:withdraws/iqd/cash/create/': {
        params: WealthCreateIqdCashWithdrawParams;
        response: WealthIqdCashWithdraw;
      };
      'post:withdraws/iqd/cash/cancel/': {
        params: {
          tracking_code: string;
        };
        response: { message: string };
      };
      'get:convert/preview/': {
        params: WealthCreateConvertParams;
        response: WealthConvertPreview;
      };
      'post:convert/': {
        params: WealthCreateConvertParams;
        response: WealthConvert;
      };
      'post:convert/{slug}/verify/': {
        params: {
          slug: string;
        };
        response: WealthConvert;
      };
      'get:pairs/{slug}/orderbook/': {
        params: {
          slug: string;
        };
        response: WealthOrderbook;
      };
      'post:orders/create/': {
        params: WealthCreateOrderParams;
      };
      'post:orders/{slug}/cancel/': {
        params: {
          slug: string;
        };
      };
      'get:coin/{slug}/deposit-networks/': {
        params: {
          slug: string;
        };
        response: WealthDepositNetwork[];
      };
      'get:coin/{slug}/withdraw-networks/': {
        params: {
          slug: string;
        };
        response: WealthWithdrawNetwork[];
      };
      'get:marketplace/merchant/cash-pickups/': {
        params: {
          status?: string;
        };
        response: WealthMerchantCashPickup[];
      };
      'get:marketplace/merchant/cash-pickups/history/': {
        response: WealthMerchantCashPickupHistory[];
      };
      'post:marketplace/merchant/cash-pickups/{slug}/verify/': {
        params: {
          slug: string;
          otp: string;
        };
        response: WealthMerchantCashPickup;
      };
      'get:marketplace/merchant/cash-deposits/': {
        params: {
          status?: string;
        };
        response: WealthMerchantCashDeposit[];
      };
      'post:marketplace/merchant/cash-deposits/{slug}/confirm/': {
        params: {
          slug: string;
        };
        response: WealthMerchantCashDeposit;
      };
      'get:withdraws/iqd/cash/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthIqdCashWithdraw;
      };
      'get:marketplace/payment-methods/': {
        response: WealthMarketplacePaymentMethod[];
      };
      'get:marketplace/ads/': {
        params: {
          ad_type?: string;
          coin?: string;
          payment_method?: string;
          amount?: string;
        };
        response: WealthMarketplaceAd[];
      };
      'get:marketplace/ads/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthMarketplaceAd;
      };
      'get:marketplace/user/payment-methods/': {
        response: {
          payment_method: WealthMarketplacePaymentMethod;
          account_details: Record<string, string>;
          is_active: boolean;
          created_at: number;
        }[];
      };
      'post:marketplace/user/payment-methods/': {
        params: {
          payment_method_slug: string;
          account_details: Record<string, string>;
        };
        response: {
          payment_method: WealthMarketplacePaymentMethod;
          account_details: Record<string, string>;
          is_active: boolean;
          created_at: number;
        };
      };
      'delete:marketplace/user/payment-methods/{slug}/': {
        params: {
          slug: string;
        };
      };
      'post:marketplace/merchant/apply/': {
        params: { display_name: string; bio?: string };
        response: WealthMarketplaceMerchant;
      };
      'get:marketplace/merchant/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthMarketplaceMerchant;
      };
      'put:marketplace/merchant/me/': {
        params: { display_name?: string; bio?: string };
        response: WealthMarketplaceMerchant;
      };
      'get:marketplace/ads/mine/': {
        params: {
          status?: string;
        };
        response: WealthMarketplaceAd[];
      };
      'post:marketplace/ads/create/': {
        params: WealthCreateMarketplaceAdParams;
        response: WealthMarketplaceAd;
      };
      'post:marketplace/ads/{slug}/pause/': {
        params: {
          slug: string;
        };
      };
      'post:marketplace/ads/{slug}/activate/': {
        params: {
          slug: string;
        };
      };
      'delete:marketplace/ads/{slug}/close/': {
        params: {
          slug: string;
        };
      };
      'post:marketplace/trades/create/': {
        params: {
          advertisement_code: string;
          fiat_amount?: string;
          crypto_amount?: string;
          payment_method_slug: string;
        };
        response: WealthMarketplaceTrade;
      };
      'get:marketplace/trades/': {
        params: {
          status?: string;
        };
        response: WealthMarketplaceTrade[];
      };
      'get:marketplace/trades/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthMarketplaceTrade;
      };
      'post:marketplace/trades/{slug}/pay/': {
        params: {
          slug: string;
          payment_proof: string;
        };
        response: WealthMarketplaceTrade;
      };
      'post:marketplace/trades/{slug}/release/': {
        params: {
          slug: string;
        };
        response: WealthMarketplaceTrade;
      };
      'post:marketplace/trades/{slug}/cancel/': {
        params: {
          slug: string;
        };
        response: WealthMarketplaceTrade;
      };
      'post:marketplace/trades/{slug}/dispute/': {
        params: {
          slug: string;
          reason: string;
        };
        response: WealthMarketplaceTrade;
      };
      'get:marketplace/trades/{slug}/messages/': {
        params: {
          slug: string;
        };
        response: {
          sender_id: string;
          message: string;
          message_type: 'TEXT' | 'IMAGE' | 'SYSTEM';
          created_at: number;
        }[];
      };
      'post:marketplace/trades/{slug}/messages/send/': {
        params: {
          slug: string;
          message: string;
          message_type?: string;
        };
        response: {
          sender_id: string;
          message: string;
          message_type: 'TEXT' | 'IMAGE' | 'SYSTEM';
          created_at: number;
        };
      };
    }>;
    'wealth-panel': ServiceEndpoints<{
      'get:deposits/crypto/': {
        params: {
          user_id?: string;
        };
        response: WealthDeposit[];
      };
      'get:deposits/iqd/': {
        params: {
          user_id?: string;
        };
        response: WealthDeposit[];
      };
      'get:withdraws/crypto/': {
        params: {
          user_id?: string;
          search?: string;
        };
        response: WealthCryptoWithdraw[];
      };
      'get:withdraws/iqd/': {
        params: {
          user_id?: string;
          search?: string;
        };
        response: WealthIqdWithdraw[];
      };
      'get:orders/': {
        params: {
          user_id?: string;
        };
        response: WealthOrder[];
      };
      'get:converts/': {
        params: {
          user_id?: string;
        };
        response: WealthConvert[];
      };
      'get:user-balances/': {
        params: {
          user_id?: string;
        };
        response: WealthUserBalanceHistory[];
      };
      'get:user-balances/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthUserBalanceHistory;
      };
      'get:user-balance/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthUserBalancePanel;
      };
      'get:marketplace/merchants/': {
        params: {
          status?: 'PENDING' | 'APPROVED' | 'SUSPENDED' | 'REJECTED';
        };
        response: WealthMarketplaceMerchant[];
      };
      'get:marketplace/merchants/{slug}/': {
        params: {
          slug: string;
        };
        response: WealthMarketplaceMerchant;
      };
      'put:marketplace/merchants/{slug}/': {
        params: {
          slug: string;
          display_name?: string;
          bio?: string;
          accepts_cash_operations?: boolean;
          balance_threshold?: string;
        };
        response: WealthMarketplaceMerchant;
      };
      'post:marketplace/merchants/{slug}/approve/': {
        params: {
          slug: string;
        };
      };
      'post:marketplace/merchants/{slug}/reject/': {
        params: {
          slug: string;
          reason: string;
        };
      };
      'get:marketplace/disputes/': {
        params: {
          status?: string;
        };
        response: WealthMarketplaceDispute[];
      };
      'post:marketplace/disputes/{slug}/resolve/': {
        params: {
          slug: string;
          resolution: WealthMarketplaceDisputeResolution;
          resolution_note?: string;
        };
      };
      'post:marketplace/merchants/{slug}/suspend/': {
        params: {
          slug: string;
        };
      };
      'get:iqd-price/': {
        response: WealthIqdPrice;
      };
      'put:iqd-price/': {
        params: Partial<WealthIqdPrice>;
      };
      'get:check-user-balance/{slug}/': {
        params: {
          slug: string;
        };
        response: {
          result: 'ok' | 'nok' | 'fatal';
        };
      };
      'get:total-user-balances/': {
        response: {
          balances: WealthBalanceSummary[];
        };
      };
    }>;
  }
}

registerService('wealth', '/proxy/wealth');
registerService('wealth-panel', '/proxy/wealth/panel');
